import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";

export default function CtaBanner({ data, site }) {
  const particles = useMemo(
    () => Array.from({ length: 24 }).map(() => ({
      left: `${Math.random() * 100}%`,
      duration: 8 + Math.random() * 10,
      delay: Math.random() * 8,
      size: 2 + Math.random() * 3,
    })),
    []
  );

  return (
    <section data-testid="cta-banner" className="relative py-20 sm:py-28 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#E6C75A] via-[#D4AF37] to-[#B8941F] p-10 sm:p-16 text-center shadow-[0_30px_80px_-20px_rgba(212,175,55,0.5)]">
          {/* white particles drifting up */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map((p, i) => (
              <span
                key={i}
                className="particle"
                style={{
                  left: p.left, bottom: "-10px",
                  width: p.size, height: p.size,
                  animationDuration: `${p.duration}s`,
                  animationDelay: `${p.delay}s`,
                }}
              />
            ))}
          </div>
          {/* radial highlight */}
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-white/20 blur-[100px] pointer-events-none" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative z-10"
          >
            {data?.eyebrow && (
              <p className="text-[11px] uppercase tracking-[0.32em] text-white/90 font-semibold mb-4">{data.eyebrow}</p>
            )}
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] drop-shadow-[0_2px_12px_rgba(0,0,0,0.15)]">
              {data?.headline_1} {data?.headline_accent && <span className="text-[#0A0A0A]">{data.headline_accent}</span>}
            </h2>
            {data?.subheadline && (
              <p className="mt-5 text-white/90 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">{data.subheadline}</p>
            )}
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href="#contact"
                data-testid="cta-banner-quote"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#0A0A0A] text-white font-display font-bold text-sm uppercase tracking-wider hover:bg-[#1A1A1A] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.4)] transition-all hover:-translate-y-0.5"
              >
                {data?.primary_cta || "Get Free Estimate"}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={site?.phone_href || "#"}
                data-testid="cta-banner-call"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#0A0A0A] font-display font-bold text-sm uppercase tracking-wider border border-white hover:bg-[#FBF8F1] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.2)] transition-all hover:-translate-y-0.5"
              >
                <Phone className="w-4 h-4" />
                {data?.secondary_cta || "Call Now"}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
